import {
  IContractBook,
  MaxUint256,
  SupportedChainId,
  contractBook,
} from "@eulerxyz/core-sdk"
import { EVCCall } from "@eulerxyz/evc-sdk"
import { Address, encodeFunctionData, getAddress } from "viem"
import { BaseTransactionFactory } from "./BaseTransactionFactory"
import { TransactionItemUnion } from "./types"
import {
  WETHTransactionItem,
  isUnwrapTransactionItem,
  isWrapTransactionItem,
} from "./types/WETHTransactionTypes"

export class WETHTransactionFactory extends BaseTransactionFactory {
  evcAddress: Address
  wrappedAddress: Address

  constructor(chainId: SupportedChainId, contractBook: IContractBook) {
    super(chainId, contractBook)
    this.evcAddress = contractBook.evc.address[chainId]
    this.wrappedAddress = getAddress(contractBook.weth.address[chainId])
  }
  typeGuardConvertMultiple(transactions: [WETHTransactionItem]): EVCCall[] {
    const transaction = transactions.at(0)
    if (
      transaction &&
      getAddress(transaction.asset.address) === this.wrappedAddress
    ) {
      if (isWrapTransactionItem(transaction)) {
        const SCALE = 10n ** 18n
        const totalShares = BigInt(transaction.totalShares || 0)
        const totalAssets = BigInt(transaction.totalAssets || 0)
        let transferValue = BigInt(transaction.value)
        if (totalShares && totalAssets) {
          transferValue =
            (transferValue * totalShares * SCALE) / (totalAssets * SCALE)
        }

        return [
          {
            data: encodeFunctionData({
              abi: this.contractBook.weth.abi,
              functionName: "deposit",
            }),
            onBehalfOfAccount: transaction.from,
            targetContract: this.wrappedAddress,
            value: BigInt(transaction.value),
          },
          {
            data: encodeFunctionData({
              abi: this.contractBook.weth.abi,
              functionName: "transfer",
              args: [transaction.vault, transferValue],
            }),
            onBehalfOfAccount: transaction.from,
            targetContract: this.wrappedAddress,
            value: 0n,
          },
          {
            targetContract: transaction.vault,
            data: encodeFunctionData({
              abi: this.contractBook.eVault.abi,
              functionName: "skim",
              args: [transferValue, transaction.to],
            }),
            onBehalfOfAccount: transaction.to,
            value: 0n,
          },
        ]
      }
      if (isUnwrapTransactionItem(transaction)) {
        return [
          {
            targetContract: transaction.vault,
            data: encodeFunctionData({
              abi: contractBook.eVault.abi,
              functionName: "withdraw",
              args: [
                BigInt(transaction.value),
                this.evcAddress,
                transaction.from,
              ],
            }),
            onBehalfOfAccount: transaction.from,
            value: 0n,
          },
          {
            data: encodeFunctionData({
              abi: this.contractBook.weth.abi,
              functionName: "withdraw",
              args: [BigInt(transaction.value)],
            }),
            onBehalfOfAccount: transaction.to,
            targetContract: this.wrappedAddress,
            value: 0n,
          },
          {
            data: "0x",
            onBehalfOfAccount: transaction.to,
            targetContract: transaction.to,
            value: BigInt(transaction.value),
          },
          ...(transaction?.isMax
            ? [
                {
                  targetContract: transaction.vault,
                  data: encodeFunctionData({
                    abi: contractBook.eVault.abi,
                    functionName: "redeem",
                    args: [MaxUint256, transaction.to, transaction.from],
                  }),
                  onBehalfOfAccount: transaction.from,
                  value: 0n,
                },
              ]
            : []),
        ]
      }
    }
    throw this.errorUnsupportedTransactionType(transaction)
  }

  typeGuardConvertSingle(transaction: TransactionItemUnion): EVCCall {
    throw this.errorUnsupportedTransactionType(transaction)
  }
}
